import { useState, useEffect, useRef, useCallback } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/components/AuthProvider';
import {
  PhoneAuthFlow,
  type PhoneAuthFlowHandle,
  type PhoneAuthStep,
} from '@/components/PhoneAuthFlow';
import { authApi } from '@/lib/api/services/auth';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { isNewSignupPending } from '@/lib/onboardingCompletion';
import { ONBOARDING_V2_PREFLIGHT_PATH } from '@/components/onboarding-v2/onboardingV2Routes';
const we_next_logo = '/assets/wenext_logo_dark.png';

// Left-panel carousel slides (illustrations live in web/public/onboardingimg).
// Each slide has its own image + caption; they auto-rotate together.
const AUTH_SLIDES = [
  {
    img: '/onboardingimg/image1.png',
    title: 'Integrate your favorite tools',
    subtitle: 'Connect Shopify, Zapier, Webflow, Stripe and more — bring your whole stack into one platform.',
    // image1's artwork fills its canvas edge-to-edge (~100%), while image2/3 are
    // exported with a built-in ~3% margin (content ≈ 94%). Scale image1 down to
    // ~94% so all three render at the same visual size. Transform-only → no
    // layout shift, stays centered in the box.
    imgClassName: 'scale-[0.94]',
  },
  {
    img: '/onboardingimg/image2.png',
    title: 'Build automations without code',
    subtitle: 'Design conversation flows with keywords, conditions, and instant replies on a visual builder.',
  },
  {
    img: '/onboardingimg/image3.png',
    title: 'Run every channel from one hub',
    subtitle: 'Manage Facebook, Instagram, LinkedIn, Meta Ads, and WhatsApp campaigns — all from WeNext.',
  },
];

export default function Auth() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  // Field-level errors for the password login form (shown under each input).
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Sign-in method: password (email) or the phone-first WhatsApp OTP flow.
  const [signInMode, setSignInMode] = useState<'password' | 'otp'>('otp');
  // Left-panel carousel index (auto-advances).
  const [slide, setSlide] = useState(0);
  // Live step of the phone-first flow → drives the header back arrow + copy.
  const [phoneStep, setPhoneStep] = useState<PhoneAuthStep>('phone');
  // Display phone (e.g. "+91 8106040901") reported by the flow so the header
  // subtitle can show "code sent to <phone>" on the code-entry step.
  const [otpPhoneDisplay, setOtpPhoneDisplay] = useState('');
  const phoneAuthRef = useRef<PhoneAuthFlowHandle>(null);

  // TOTP State
  const [requiresTotp, setRequiresTotp] = useState(false);
  const [totpUserId, setTotpUserId] = useState('');
  const [totpCode, setTotpCode] = useState('');
  // Forgot-password in-page view (renders in the same right panel as login/signup).
  const [forgotPassword, setForgotPassword] = useState(false);
  const [fpEmail, setFpEmail] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState('');
  const [fpSuccess, setFpSuccess] = useState(false);
  const { user, login, verifyLoginTotp } = useAuth();

  useEffect(() => {
    // Check if user is already authenticated
    if (user) {
      // Agency users go to agency dashboard
      if ((user as any)?.is_agency) {
        navigate('/home/agency/dashboard');
      } else if (isNewSignupPending()) {
        // Brand-new account: the questions are the next step. Do NOT send them
        // to /home — that races with signup's own navigate and can drop them on
        // Overview / the setup plan before the first-run gate runs.
        navigate(ONBOARDING_V2_PREFLIGHT_PATH);
      } else {
        // Returning users go to home (the first-run gate redirects if needed)
        navigate('/home');
      }
    }
  }, [user, navigate]);

  // Auto-advance the left-panel image carousel every ~4.5s.
  useEffect(() => {
    const timer = setInterval(() => {
      setSlide((s) => (s + 1) % AUTH_SLIDES.length);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  // Stable identity: PhoneAuthFlow lists this in an effect's deps, so a fresh
  // arrow each render would re-run that effect on every render.
  const handlePhoneStepChange = useCallback((step: PhoneAuthStep, phone?: string) => {
    setPhoneStep(step);
    if (phone) setOtpPhoneDisplay(phone);
  }, []);

  // Shared post-login redirect (used by password AND phone-OTP login).
  const redirectAfterLogin = () => {
    localStorage.setItem('showBillingDialog', 'true');
    setTimeout(() => {
      const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
      if (currentUser?.is_agency) {
        navigate('/home/agency/dashboard');
      } else {
        navigate('/home');
      }
    }, 100);
  };

  // The password field accepts an email OR a phone number. Resolve it into the
  // identifier the backend expects: emails pass through lowercased; phone
  // numbers become E.164 (a bare number defaults to +91, the app's default
  // country — include a +<code> prefix for other countries).
  const normalizeLoginIdentifier = (raw: string) => {
    const v = (raw || '').trim();
    if (!v) return v;
    if (v.includes('@')) return v.toLowerCase();
    if (v.startsWith('+')) return v.replace(/\s+/g, '');
    const digits = v.replace(/\D/g, '');
    if (digits.length >= 6) return `+91${digits}`;
    return v;
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    // Field-level validation — surface the message under each input.
    let hasError = false;
    if (!email.trim()) {
      setEmailError('Please enter your email or phone number');
      hasError = true;
    }
    if (!password) {
      setPasswordError('Please enter your password');
      hasError = true;
    }
    if (hasError) return;

    setLoading(true);
    setError('');

    try {
      const result = await login(normalizeLoginIdentifier(email), password);

      if (result.success) {
        if (result.requires_totp && result.userId) {
          setRequiresTotp(true);
          setTotpUserId(result.userId);
          setLoading(false);
          return;
        }

        redirectAfterLogin();
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (error: any) {
      setError(friendlyMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await verifyLoginTotp(totpUserId, totpCode);

      if (result.success) {
        // Set flag to show billing dialog after redirect
        localStorage.setItem('showBillingDialog', 'true');
        setTimeout(() => {
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          if (currentUser?.is_agency) {
            navigate('/home/agency/dashboard');
          } else {
            navigate('/home');
          }
        }, 100);
      } else {
        setError(result.error || 'Invalid code');
      }
    } catch (error: any) {
      setError(friendlyMessage(error, 'Verification failed'));
    } finally {
      setLoading(false);
    }
  };

  // Leave the forgot-password view and reset its state.
  const exitForgotPassword = () => {
    setForgotPassword(false);
    setFpError('');
    setFpSuccess(false);
    setFpEmail('');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setFpLoading(true);
    setFpError('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(fpEmail)) {
      setFpError('Please enter a valid email address');
      setFpLoading(false);
      return;
    }

    try {
      const result = await authApi.requestPasswordReset({ email: fpEmail });
      if (result.error) {
        setFpError(result.error);
      } else {
        setFpSuccess(true);
        toast({
          title: 'Email sent!',
          description: 'Check your email for password reset instructions.',
        });
      }
    } catch (error: any) {
      setFpError(friendlyMessage(error, 'Failed to send reset email. Please try again.'));
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-white lg:grid lg:h-screen lg:grid-cols-2 lg:overflow-hidden">
      {/* LEFT — brand carousel (hidden on mobile) */}
      <div className="relative hidden overflow-hidden lg:flex lg:pl-6 lg:pt-6 lg:pb-6">
        <div
          className="relative flex flex-1 flex-col items-center justify-center overflow-hidden rounded-3xl p-12"
          style={{ background: 'linear-gradient(160deg, #17a866 0%, #0c8a4f 45%, #0a6a3d 100%)' }}
        >
        <div className="relative z-10 flex w-full max-w-lg flex-col items-center">
          {/* Rotating illustration */}
          <div className="relative aspect-[4/3] w-full">
            {AUTH_SLIDES.map((s, i) => (
              <img
                key={s.img}
                src={s.img}
                alt=""
                aria-hidden={i !== slide}
                className={cn(
                  'absolute inset-0 h-full w-full rounded-2xl object-contain transition-opacity duration-700 ease-out',
                  (s as { imgClassName?: string }).imgClassName,
                  i === slide ? 'opacity-100' : 'opacity-0'
                )}
              />
            ))}
          </div>

          {/* Caption changes with the active slide (keyed so it re-fades). */}
          <div key={slide} className="animate-in fade-in duration-700">
            <h2 className="mt-10 text-center text-3xl font-extrabold leading-tight text-white">
              {AUTH_SLIDES[slide].title}
            </h2>
            <p className="mt-3 mx-auto max-w-md text-center text-sm leading-relaxed text-white/80">
              {AUTH_SLIDES[slide].subtitle}
            </p>
          </div>

          {/* Dots */}
          <div className="mt-6 flex items-center gap-2">
            {AUTH_SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => setSlide(i)}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-300',
                  i === slide ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/60'
                )}
              />
            ))}
          </div>
        </div>
        </div>
      </div>

      {/* RIGHT — auth */}
      <div className="flex min-h-screen flex-col bg-white px-6 py-10 sm:px-10">
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
          <img src={we_next_logo} alt="wenext" className="mb-8 w-[132px]" />

          {forgotPassword ? (
            <>
              <button
                type="button"
                aria-label="Back"
                onClick={exitForgotPassword}
                className="mb-3 -ml-1 inline-flex h-9 w-fit items-center gap-1.5 rounded-lg px-1 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>

              <h1 className="text-2xl font-bold text-gray-900">
                {fpSuccess ? 'Check your email' : 'Forgot password?'}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {fpSuccess ? (
                  <>
                    We've sent a password reset link to{' '}
                    <span className="font-semibold">{fpEmail}</span>.
                  </>
                ) : (
                  "Enter your email address and we'll send you a link to reset your password."
                )}
              </p>

              {fpSuccess ? (
                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4">
                    <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-green-500" />
                    <div className="text-sm text-gray-600">
                      <p>If you don't see the email, check your spam folder.</p>
                      <p className="mt-1 text-xs text-gray-500">The reset link will expire in 1 hour.</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full !h-12"
                    onClick={() => { setFpSuccess(false); setFpEmail(''); }}
                  >
                    Send another email
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={exitForgotPassword}
                  >
                    Back to login
                  </Button>
                </div>
              ) : (
                <>
                  {fpError && (
                    <Alert variant="destructive" className="mt-4">
                      <AlertDescription>{fpError}</AlertDescription>
                    </Alert>
                  )}
                  <form onSubmit={handleForgotPassword} noValidate className="mt-6 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="fp-email">Email</Label>
                      <Input
                        id="fp-email"
                        type="email"
                        placeholder="your@email.com"
                        value={fpEmail}
                        onChange={(e) => { setFpEmail(e.target.value); if (fpError) setFpError(''); }}
                        required
                        autoFocus
                        disabled={fpLoading}
                        className="h-12"
                      />
                    </div>
                    <Button type="submit" className="w-full !h-12" disabled={fpLoading || !fpEmail.trim()}>
                      {fpLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {fpLoading ? 'Sending...' : 'Send reset link'}
                    </Button>
                  </form>
                </>
              )}
            </>
          ) : requiresTotp ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900">Two-factor authentication</h1>
              <p className="mt-1 text-sm text-gray-500">Enter the code from your authenticator app.</p>
              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <form onSubmit={handleVerifyTotp} className="mt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="totpCode">Verification code</Label>
                  <Input
                    id="totpCode"
                    type="text"
                    placeholder="123456"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="h-12 text-center text-lg tracking-widest"
                    required
                    autoFocus
                  />
                </div>
                <Button type="submit" className="w-full !h-12" disabled={loading || totpCode.length !== 6}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Verify
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setRequiresTotp(false);
                    setTotpUserId('');
                    setTotpCode('');
                    setError('');
                  }}
                >
                  Back to login
                </Button>
              </form>
            </>
          ) : (
            <>
              {signInMode === 'otp' && phoneStep !== 'phone' && (
                <button
                  type="button"
                  aria-label="Back"
                  onClick={() => phoneAuthRef.current?.back()}
                  className="mb-3 -ml-1 inline-flex h-9 w-fit items-center gap-1.5 rounded-lg px-1 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              )}

              <h1 className="text-2xl font-bold text-gray-900">
                {signInMode === 'password'
                  ? 'Welcome back'
                  : phoneStep === 'otp'
                    ? 'Verify your account'
                    : phoneStep === 'name'
                      ? 'Name your organization'
                      : 'Log in or sign up'}
              </h1>

              <p className="mt-1 text-sm text-gray-500">
                {signInMode === 'otp'
                  ? (phoneStep === 'otp'
                      ? (
                          <>
                            We've sent a 6-digit verification code to{' '}
                            <span className="font-semibold">{otpPhoneDisplay}</span> on WhatsApp.
                          </>
                        )
                      : phoneStep === 'name'
                        ? 'This is the business or team your workspace belongs to.'
                        : "Enter your phone number — we'll send a code on WhatsApp.")
                  : 'Login with your email or phone and password.'}
              </p>

              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="mt-6">
                {signInMode === 'otp' ? (
                  <PhoneAuthFlow
                    ref={phoneAuthRef}
                    onStepChange={handlePhoneStepChange}
                    onRequiresTotp={(userId) => { setRequiresTotp(true); setTotpUserId(userId); }}
                    onSuccess={redirectAfterLogin}
                    onSwitchToPassword={(message) => { setSignInMode('password'); setError(message); }}
                  />
                ) : (
                  <form onSubmit={handleSignIn} noValidate className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email/phone</Label>
                      <Input
                        id="email"
                        type="text"
                        placeholder="Enter email or phone number"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) setEmailError('');
                        }}
                        aria-invalid={!!emailError}
                        required
                        className={cn('h-12', emailError && 'border-red-400 focus-visible:ring-red-400')}
                      />
                      {emailError && <p className="text-xs text-red-600">{emailError}</p>}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        <button
                          type="button"
                          onClick={() => { setForgotPassword(true); setError(''); }}
                          className="text-xs text-primary hover:underline"
                        >
                          Forgot password?
                        </button>
                      </div>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Your password"
                          value={password}
                          onChange={(e) => {
                            setPassword(e.target.value);
                            if (passwordError) setPasswordError('');
                          }}
                          aria-invalid={!!passwordError}
                          required
                          className={cn('h-12 pr-10', passwordError && 'border-red-400 focus-visible:ring-red-400')}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                      {passwordError && <p className="text-xs text-red-600">{passwordError}</p>}
                    </div>
                    <Button type="submit" className="w-full !h-12 font-bold text-[16px]" disabled={loading}>
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Login
                    </Button>
                  </form>
                )}
              </div>

              {/* Alternate-login link. There is deliberately NO "Create Account"
                  link any more: the phone screen IS both paths, so a second
                  entry point would just be duplicated navigation. Hidden past
                  the phone screen — the back arrow returns to where it belongs,
                  and mid-signup (the name screen) it is not an option. */}
              {(signInMode === 'password' || phoneStep === 'phone') && (
                <p className="mt-4 text-center text-sm text-gray-500">
                  {signInMode === 'otp' ? (
                    <>
                      Prefer email?{' '}
                      <button
                        type="button"
                        onClick={() => { setSignInMode('password'); setError(''); }}
                        className="font-semibold text-[var(--secondary-color)] hover:underline"
                      >
                        Login with password
                      </button>
                    </>
                  ) : (
                    <>
                      Prefer phone?{' '}
                      <button
                        type="button"
                        onClick={() => { setSignInMode('otp'); setError(''); }}
                        className="font-semibold text-[var(--secondary-color)] hover:underline"
                      >
                        Login with OTP
                      </button>
                    </>
                  )}
                </p>
              )}
            </>
          )}
        </div>

        
      </div>
    </div>
  );
}
